import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import { BLOG_POSTS, BlogPost } from '../../data/blogContent';
import './ContentHub.css';

type SubTab = 'create' | 'calendar' | 'posts' | 'drafts' | 'blog';
type PostStatus = 'draft' | 'scheduled' | 'published' | 'failed';
type Platform = 'facebook' | 'linkedin';

interface GeneratedContent {
  content: string;
  hashtags: string[];
  suggestedImagePrompt: string;
}

interface Post {
  postId: string;
  platform: Platform;
  content: string;
  status: PostStatus;
  scheduledDate?: string;
  publishedDate?: string;
  imageUrl?: string;
  createdAt: string;
  updatedAt?: string;
}

export const ContentHub: React.FC = () => {
  const { getIdToken } = useAuth();
  const [subTab, setSubTab] = useState<SubTab>('create');

  // Store selection
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('');

  // Create state
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [platforms, setPlatforms] = useState<Platform[]>(['facebook', 'linkedin']);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState<Record<string, GeneratedContent>>({});
  const [editedContent, setEditedContent] = useState<Record<string, string>>({});
  const [generatedImages, setGeneratedImages] = useState<Record<string, string>>({});
  const [generatingImage, setGeneratingImage] = useState<Record<string, boolean>>({});
  const [refinement, setRefinement] = useState('');
  const [error, setError] = useState('');

  // Posts state
  const [posts, setPosts] = useState<Post[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');

  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Schedule modal state
  const [scheduleModal, setScheduleModal] = useState<{ platform: string } | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');

  // Publishing state
  const [publishing, setPublishing] = useState<Record<string, boolean>>({});

  // Blog library state
  const [blogSearch, setBlogSearch] = useState('');
  const [blogCategory, setBlogCategory] = useState('all');
  const [blogSeries, setBlogSeries] = useState('all');
  const [expandedBlog, setExpandedBlog] = useState<string | null>(null);

  // Editable image prompts per platform
  const [imagePrompts, setImagePrompts] = useState<Record<string, string>>({});

  // Blog-sourced image prompt (carried from Blog tab via "Use as Topic")
  const [blogImagePrompt, setBlogImagePrompt] = useState('');

  // Source URL for linking posts back to blog content
  const [sourceUrl, setSourceUrl] = useState('https://bigsystemone.com');
  const [includeLink, setIncludeLink] = useState(true);

  const authHeaders = useCallback(async () => {
    const token = await getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }, [getIdToken]);

  // Fetch stores on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await authenticatedFetch(API_ENDPOINTS.stores());
        if (response.ok) {
          const data = await response.json();
          const storeList = Array.isArray(data) ? data : (data.data || data.stores || []);
          setStores(storeList);
          if (storeList.length > 0) {
            setSelectedStore(storeList[0].storeId);
          }
        }
      } catch (err) {
        console.error('Failed to fetch stores:', err);
      }
    };
    fetchStores();
  }, []);

  const storeId = selectedStore;

  // Fetch posts
  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${API_ENDPOINTS.socialMediaPosts()}?storeId=${storeId}`,
        { headers }
      );
      const data = await res.json();
      if (data.success) {
        setPosts(data.posts || []);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setPostsLoading(false);
    }
  }, [authHeaders, storeId]);

  useEffect(() => {
    if (storeId && (subTab === 'posts' || subTab === 'drafts' || subTab === 'calendar')) {
      fetchPosts();
    }
  }, [subTab, storeId, fetchPosts]);

  // Generate content via OpenAI
  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(API_ENDPOINTS.socialMediaGenerateContent(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ topic, tone, length, platforms }),
      });
      const data = await res.json();
      if (data.success) {
        setGenerated(data.content);
        const edited: Record<string, string> = {};
        const prompts: Record<string, string> = {};
        for (const p of Object.keys(data.content)) {
          edited[p] = data.content[p].content;
          // Use blog image prompt if available, otherwise use AI-suggested prompt
          prompts[p] = blogImagePrompt || data.content[p].suggestedImagePrompt || '';
        }
        setEditedContent(edited);
        setImagePrompts(prompts);
      } else {
        setError(data.error || 'Failed to generate content');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  // Refine content
  const handleRefine = async () => {
    if (!refinement.trim()) return;
    setGenerating(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(API_ENDPOINTS.socialMediaGenerateContent(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ topic, tone, length, platforms, refinement }),
      });
      const data = await res.json();
      if (data.success) {
        setGenerated(data.content);
        const edited: Record<string, string> = {};
        const prompts: Record<string, string> = {};
        for (const p of Object.keys(data.content)) {
          edited[p] = data.content[p].content;
          prompts[p] = imagePrompts[p] || data.content[p].suggestedImagePrompt || '';
        }
        setEditedContent(edited);
        setImagePrompts(prompts);
        setRefinement('');
      } else {
        setError(data.error || 'Failed to refine content');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to refine content');
    } finally {
      setGenerating(false);
    }
  };

  // Generate image via DALL-E
  const handleGenerateImage = async (platform: string) => {
    const prompt = imagePrompts[platform] || generated[platform]?.suggestedImagePrompt;
    if (!prompt) return;
    setGeneratingImage(prev => ({ ...prev, [platform]: true }));
    try {
      const headers = await authHeaders();
      const res = await fetch(API_ENDPOINTS.generateImage(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ prompt, itemName: topic }),
      });
      const data = await res.json();
      if (data.imageUrl || data.url) {
        setGeneratedImages(prev => ({ ...prev, [platform]: data.imageUrl || data.url }));
      }
    } catch (err) {
      console.error('Image generation failed:', err);
    } finally {
      setGeneratingImage(prev => ({ ...prev, [platform]: false }));
    }
  };

  // Save as draft
  const handleSaveDraft = async (platform: string) => {
    try {
      const headers = await authHeaders();
      await fetch(API_ENDPOINTS.socialMediaPosts(), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          storeId: storeId,
          enterpriseId: storeId,
          platform,
          content: editedContent[platform] || generated[platform]?.content,
          status: 'draft',
          imageUrl: generatedImages[platform] || '',
        }),
      });
      fetchPosts();
    } catch (err) {
      console.error('Failed to save draft:', err);
    }
  };

  // Schedule post
  const handleSchedule = async (platform: string) => {
    setScheduleModal({ platform });
  };

  const confirmSchedule = async () => {
    if (!scheduleModal || !scheduleDate) return;
    const { platform } = scheduleModal;
    try {
      const headers = await authHeaders();
      await fetch(API_ENDPOINTS.socialMediaPosts(), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          storeId: storeId,
          enterpriseId: storeId,
          platform,
          content: editedContent[platform] || generated[platform]?.content,
          status: 'scheduled',
          scheduledDate: new Date(scheduleDate).toISOString(),
          imageUrl: generatedImages[platform] || '',
        }),
      });
      setScheduleModal(null);
      setScheduleDate('');
      fetchPosts();
    } catch (err) {
      console.error('Failed to schedule post:', err);
    }
  };

  // Publish now
  const handlePublish = async (platform: string) => {
    setPublishing(prev => ({ ...prev, [platform]: true }));
    try {
      const headers = await authHeaders();
      const content = editedContent[platform] || generated[platform]?.content;

      if (platform === 'facebook') {
        // Get connected Facebook page
        const acctRes = await fetch(
          `${API_ENDPOINTS.socialMediaAccounts()}?storeId=${storeId}`,
          { headers }
        );
        const acctData = await acctRes.json();
        const fbAccount = (acctData.data || acctData.accounts || []).find(
          (a: any) => a.platform === 'facebook' && a.isActive
        );
        if (!fbAccount?.pageId && !fbAccount?.platformAccountId) {
          setError('No connected Facebook page found. Connect one in Social Media > Facebook.');
          setPublishing(prev => ({ ...prev, [platform]: false }));
          return;
        }
        const pageId = fbAccount.pageId || fbAccount.platformAccountId;
        await fetch(API_ENDPOINTS.facebookPagePosts(pageId), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            message: content,
            storeId: storeId,
            picture: generatedImages[platform] || undefined,
            link: includeLink ? sourceUrl : undefined,
          }),
        });
      } else if (platform === 'linkedin') {
        const liRes = await fetch(API_ENDPOINTS.linkedinPost(), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            text: content,
            storeId: storeId,
            imageUrls: generatedImages[platform] ? [generatedImages[platform]] : undefined,
            link: includeLink ? sourceUrl : undefined,
          }),
        });
        const liData = await liRes.json();
        if (!liRes.ok || liData.error) {
          throw new Error(liData.error || `LinkedIn publish failed (${liRes.status})`);
        }
      }

      // Save record as published
      await fetch(API_ENDPOINTS.socialMediaPosts(), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          storeId: storeId,
          enterpriseId: storeId,
          platform,
          content,
          status: 'published',
          publishedDate: new Date().toISOString(),
          imageUrl: generatedImages[platform] || '',
        }),
      });
      fetchPosts();
    } catch (err: any) {
      setError(`Publish failed: ${err.message}`);
    } finally {
      setPublishing(prev => ({ ...prev, [platform]: false }));
    }
  };

  // Publish existing post
  const handlePublishPost = async (post: Post) => {
    setPublishing(prev => ({ ...prev, [post.postId]: true }));
    try {
      const headers = await authHeaders();

      if (post.platform === 'facebook') {
        const acctRes = await fetch(
          `${API_ENDPOINTS.socialMediaAccounts()}?storeId=${storeId}`,
          { headers }
        );
        const acctData = await acctRes.json();
        const fbAccount = (acctData.data || acctData.accounts || []).find(
          (a: any) => a.platform === 'facebook' && a.isActive
        );
        if (!fbAccount?.pageId && !fbAccount?.platformAccountId) {
          setError('No connected Facebook page found.');
          return;
        }
        await fetch(API_ENDPOINTS.facebookPagePosts(fbAccount.pageId || fbAccount.platformAccountId), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            message: post.content,
            storeId: storeId,
            picture: post.imageUrl || undefined,
            link: includeLink ? sourceUrl : undefined,
          }),
        });
      } else if (post.platform === 'linkedin') {
        await fetch(API_ENDPOINTS.linkedinPost(), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            text: post.content,
            storeId: storeId,
            imageUrls: post.imageUrl ? [post.imageUrl] : undefined,
            link: includeLink ? sourceUrl : undefined,
          }),
        });
      }

      // Update status
      await fetch(API_ENDPOINTS.socialMediaPost(post.postId), {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          status: 'published',
          publishedDate: new Date().toISOString(),
        }),
      });
      fetchPosts();
    } catch (err: any) {
      setError(`Publish failed: ${err.message}`);
    } finally {
      setPublishing(prev => ({ ...prev, [post.postId]: false }));
    }
  };

  // Delete post
  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      const headers = await authHeaders();
      await fetch(API_ENDPOINTS.socialMediaPost(postId), {
        method: 'DELETE',
        headers,
      });
      fetchPosts();
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  // Toggle platform
  const togglePlatform = (p: Platform) => {
    setPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const days: { date: string; day: number; isCurrentMonth: boolean; isToday: boolean }[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      days.push({
        date: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
        day: d,
        isCurrentMonth: false,
        isToday: false,
      });
    }

    const today = new Date();
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({
        date: dateStr,
        day: d,
        isCurrentMonth: true,
        isToday: today.getFullYear() === year && today.getMonth() === month && today.getDate() === d,
      });
    }

    const remaining = 42 - days.length;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let d = 1; d <= remaining; d++) {
      days.push({
        date: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
        day: d,
        isCurrentMonth: false,
        isToday: false,
      });
    }
    return days;
  };

  const getPostsForDate = (dateStr: string) => {
    return posts.filter(p => {
      const d = p.scheduledDate || p.publishedDate || p.createdAt;
      return d && d.startsWith(dateStr);
    });
  };

  const filteredPosts = posts.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (platformFilter !== 'all' && p.platform !== platformFilter) return false;
    return true;
  });

  const drafts = posts.filter(p => p.status === 'draft');

  const monthLabel = calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const formatDate = (iso: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  return (
    <div className="content-hub">
      <div className="content-hub-tabs">
        {([
          ['create', 'Create'],
          ['blog', `Blog (${BLOG_POSTS.length})`],
          ['calendar', 'Calendar'],
          ['posts', 'Posts'],
          ['drafts', 'Drafts'],
        ] as [SubTab, string][]).map(([key, label]) => (
          <button
            key={key}
            className={`content-hub-tab ${subTab === key ? 'active' : ''}`}
            onClick={() => setSubTab(key)}
          >
            {label}
          </button>
        ))}
        {stores.length > 1 && (
          <select
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            style={{ marginLeft: 'auto', padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
          >
            {stores.map(s => (
              <option key={s.storeId} value={s.storeId}>{s.name}</option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <div className="ch-error">
          {error}
          <button
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
            onClick={() => setError('')}
          >
            X
          </button>
        </div>
      )}

      {/* CREATE TAB */}
      {subTab === 'create' && (
        <div className="ch-create">
          <div className="ch-create-form">
            <h3>Generate AI Content</h3>
            <textarea
              className="ch-topic-input"
              placeholder="Enter a topic or idea for your post... (e.g., 'How BSO helps restaurants reduce food waste by 30%')"
              value={topic}
              onChange={e => setTopic(e.target.value)}
            />
            <div className="ch-settings-row">
              <div className="ch-setting">
                <label>Tone</label>
                <select value={tone} onChange={e => setTone(e.target.value)}>
                  <option value="professional">Professional</option>
                  <option value="conversational">Conversational</option>
                  <option value="inspirational">Inspirational</option>
                </select>
              </div>
              <div className="ch-setting">
                <label>Length</label>
                <select value={length} onChange={e => setLength(e.target.value)}>
                  <option value="short">Short</option>
                  <option value="medium">Medium</option>
                  <option value="long">Long</option>
                </select>
              </div>
              <div className="ch-setting">
                <label>Platforms</label>
                <div className="ch-platforms">
                  <label className="ch-platform-check">
                    <input
                      type="checkbox"
                      checked={platforms.includes('facebook')}
                      onChange={() => togglePlatform('facebook')}
                    />
                    <span className="facebook">Facebook</span>
                  </label>
                  <label className="ch-platform-check">
                    <input
                      type="checkbox"
                      checked={platforms.includes('linkedin')}
                      onChange={() => togglePlatform('linkedin')}
                    />
                    <span className="linkedin">LinkedIn</span>
                  </label>
                </div>
              </div>
              <button
                className="ch-generate-btn"
                onClick={handleGenerate}
                disabled={generating || !topic.trim() || platforms.length === 0}
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>

          {/* Generated Content Preview */}
          {Object.keys(generated).length > 0 && (
            <div className="ch-preview-cards">
              {Object.entries(generated).map(([platform, data]) => (
                <div key={platform} className="ch-preview-card">
                  <div className={`ch-preview-header ${platform}`}>
                    <span className={`ch-platform-label ${platform}`}>
                      {platform === 'facebook' ? 'Facebook' : 'LinkedIn'}
                    </span>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {(editedContent[platform] || '').length} chars
                    </span>
                  </div>
                  <div className="ch-preview-body">
                    <textarea
                      className="ch-preview-textarea"
                      value={editedContent[platform] || ''}
                      onChange={e =>
                        setEditedContent(prev => ({ ...prev, [platform]: e.target.value }))
                      }
                    />
                    {data.hashtags?.length > 0 && (
                      <div className="ch-hashtags">
                        {data.hashtags.map((tag, i) => (
                          <span key={i} className="ch-hashtag">#{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="ch-image-section">
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#475569', marginBottom: 4 }}>Image Prompt</label>
                      <textarea
                        style={{ width: '100%', minHeight: 60, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, color: '#334155', resize: 'vertical', marginBottom: 8, fontFamily: 'inherit' }}
                        value={imagePrompts[platform] || ''}
                        onChange={e => setImagePrompts(prev => ({ ...prev, [platform]: e.target.value }))}
                        placeholder="Describe the image you want to generate..."
                      />
                      <button
                        className="ch-image-btn"
                        onClick={() => handleGenerateImage(platform)}
                        disabled={generatingImage[platform] || !(imagePrompts[platform] || '').trim()}
                      >
                        {generatingImage[platform]
                          ? 'Generating Image...'
                          : generatedImages[platform]
                          ? 'Regenerate Image'
                          : 'Generate Image'}
                      </button>
                      {generatedImages[platform] && (
                        <div className="ch-image-preview">
                          <img src={generatedImages[platform]} alt="Generated" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ch-link-option">
                    <label className="ch-platform-check">
                      <input
                        type="checkbox"
                        checked={includeLink}
                        onChange={() => setIncludeLink(!includeLink)}
                      />
                      <span style={{ fontSize: 13, color: '#475569' }}>Include link to</span>
                    </label>
                    {includeLink && (
                      <input
                        type="text"
                        value={sourceUrl}
                        onChange={e => setSourceUrl(e.target.value)}
                        style={{ flex: 1, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, color: '#334155' }}
                      />
                    )}
                  </div>
                  <div className="ch-preview-actions">
                    <button className="ch-btn" onClick={() => handleSaveDraft(platform)}>
                      Save as Draft
                    </button>
                    <button className="ch-btn" onClick={() => handleSchedule(platform)}>
                      Schedule
                    </button>
                    <button
                      className="ch-btn ch-btn-primary"
                      onClick={() => handlePublish(platform)}
                      disabled={publishing[platform]}
                    >
                      {publishing[platform] ? 'Publishing...' : 'Publish Now'}
                    </button>
                  </div>
                </div>
              ))}

              {/* Refinement */}
              <div className="ch-refinement">
                <h4>Refine Content</h4>
                <div className="ch-refinement-row">
                  <input
                    className="ch-refinement-input"
                    placeholder="Describe how you'd like to change the content..."
                    value={refinement}
                    onChange={e => setRefinement(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleRefine(); }}
                  />
                  <button
                    className="ch-btn ch-btn-primary"
                    onClick={handleRefine}
                    disabled={generating || !refinement.trim()}
                  >
                    {generating ? 'Refining...' : 'Refine'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CALENDAR TAB */}
      {subTab === 'calendar' && (
        <div>
          <div className="ch-calendar">
            <div className="ch-calendar-header">
              <button
                className="ch-calendar-nav"
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}
              >
                Prev
              </button>
              <h3>{monthLabel}</h3>
              <button
                className="ch-calendar-nav"
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}
              >
                Next
              </button>
            </div>
            <div className="ch-calendar-grid">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="ch-calendar-day-header">{d}</div>
              ))}
              {getDaysInMonth(calendarDate).map((day, i) => {
                const dayPosts = getPostsForDate(day.date);
                return (
                  <div
                    key={i}
                    className={`ch-calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''}`}
                    onClick={() => setSelectedDay(day.date)}
                  >
                    <div className="ch-calendar-day-number">{day.day}</div>
                    <div className="ch-calendar-dots">
                      {dayPosts.map((p, j) => (
                        <div key={j} className={`ch-calendar-dot ${p.platform} ${p.status}`} title={`${p.platform} - ${p.status}`} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedDay && (
            <div className="ch-day-detail">
              <h4>
                Posts for {new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </h4>
              {getPostsForDate(selectedDay).length === 0 ? (
                <div className="ch-day-detail-empty">No posts on this day</div>
              ) : (
                <div className="ch-post-list">
                  {getPostsForDate(selectedDay).map(p => (
                    <div key={p.postId} className="ch-post-card">
                      <div className="ch-post-content">
                        <p>{p.content}</p>
                        <div className="ch-post-meta">
                          <span className={`ch-badge ${p.platform}`}>{p.platform}</span>
                          <span className={`ch-badge ${p.status}`}>{p.status}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* POSTS TAB */}
      {subTab === 'posts' && (
        <div>
          <div className="ch-posts-controls">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="failed">Failed</option>
            </select>
            <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)}>
              <option value="all">All Platforms</option>
              <option value="facebook">Facebook</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </div>

          {postsLoading ? (
            <div className="ch-loading">Loading posts...</div>
          ) : filteredPosts.length === 0 ? (
            <div className="ch-empty">No posts found. Create one in the Create tab.</div>
          ) : (
            <div className="ch-post-list">
              {filteredPosts.map(p => (
                <div key={p.postId} className="ch-post-card">
                  {p.imageUrl && <img src={p.imageUrl} alt="" className="ch-post-image" />}
                  <div className="ch-post-content">
                    <p>{p.content}</p>
                    <div className="ch-post-meta">
                      <span className={`ch-badge ${p.platform}`}>{p.platform}</span>
                      <span className={`ch-badge ${p.status}`}>{p.status}</span>
                      <span className="ch-post-date">
                        {p.publishedDate
                          ? `Published ${formatDate(p.publishedDate)}`
                          : p.scheduledDate
                          ? `Scheduled ${formatDate(p.scheduledDate)}`
                          : `Created ${formatDate(p.createdAt)}`}
                      </span>
                    </div>
                  </div>
                  <div className="ch-post-actions">
                    {(p.status === 'draft' || p.status === 'scheduled') && (
                      <button
                        className="ch-btn ch-btn-primary"
                        onClick={() => handlePublishPost(p)}
                        disabled={publishing[p.postId]}
                      >
                        {publishing[p.postId] ? '...' : 'Publish'}
                      </button>
                    )}
                    {p.status === 'failed' && (
                      <button
                        className="ch-btn ch-btn-primary"
                        onClick={() => handlePublishPost(p)}
                        disabled={publishing[p.postId]}
                      >
                        Retry
                      </button>
                    )}
                    <button className="ch-btn ch-btn-danger" onClick={() => handleDeletePost(p.postId)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* DRAFTS TAB */}
      {subTab === 'drafts' && (
        <div>
          {postsLoading ? (
            <div className="ch-loading">Loading drafts...</div>
          ) : drafts.length === 0 ? (
            <div className="ch-empty">No drafts saved. Generate content and save as draft.</div>
          ) : (
            <div className="ch-post-list">
              {drafts.map(p => (
                <div key={p.postId} className="ch-post-card">
                  {p.imageUrl && <img src={p.imageUrl} alt="" className="ch-post-image" />}
                  <div className="ch-post-content">
                    <p>{p.content}</p>
                    <div className="ch-post-meta">
                      <span className={`ch-badge ${p.platform}`}>{p.platform}</span>
                      <span className={`ch-badge draft`}>Draft</span>
                      <span className="ch-post-date">Created {formatDate(p.createdAt)}</span>
                    </div>
                  </div>
                  <div className="ch-post-actions">
                    <button
                      className="ch-btn ch-btn-primary"
                      onClick={() => handlePublishPost(p)}
                      disabled={publishing[p.postId]}
                    >
                      {publishing[p.postId] ? '...' : 'Publish'}
                    </button>
                    <button className="ch-btn ch-btn-danger" onClick={() => handleDeletePost(p.postId)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* BLOG LIBRARY TAB */}
      {subTab === 'blog' && (() => {
        const categories = Array.from(new Set(BLOG_POSTS.map(b => b.category))).sort();
        const series = Array.from(new Set(BLOG_POSTS.map(b => b.series))).sort();
        const filtered = BLOG_POSTS.filter(b => {
          if (blogCategory !== 'all' && b.category !== blogCategory) return false;
          if (blogSeries !== 'all' && b.series !== blogSeries) return false;
          if (blogSearch && !b.title.toLowerCase().includes(blogSearch.toLowerCase()) && !b.hook.toLowerCase().includes(blogSearch.toLowerCase())) return false;
          return true;
        });
        return (
          <div>
            <div className="ch-posts-controls" style={{ gap: 8, flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search blog posts..."
                value={blogSearch}
                onChange={e => setBlogSearch(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, minWidth: 200 }}
              />
              <select value={blogSeries} onChange={e => setBlogSeries(e.target.value)}>
                <option value="all">All Series</option>
                {series.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={blogCategory} onChange={e => setBlogCategory(e.target.value)}>
                <option value="all">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <span style={{ fontSize: 13, color: '#64748b', alignSelf: 'center' }}>{filtered.length} posts</span>
            </div>
            <div className="ch-post-list">
              {filtered.map(blog => (
                <div key={blog.id} className="ch-post-card" style={{ cursor: 'pointer' }} onClick={() => setExpandedBlog(expandedBlog === blog.id ? null : blog.id)}>
                  <div className="ch-post-content" style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span className="ch-badge linkedin" style={{ fontSize: 11 }}>{blog.series}</span>
                      <span className="ch-badge scheduled" style={{ fontSize: 11 }}>{blog.category}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{blog.audience}</span>
                    </div>
                    <p style={{ fontWeight: 600, marginBottom: 4, fontSize: 15, color: '#1e293b' }}>{blog.title}</p>
                    <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>{blog.hook}</p>
                    {expandedBlog === blog.id && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                        <div style={{ marginBottom: 10 }}>
                          <strong style={{ fontSize: 12, color: '#475569' }}>Key Points:</strong>
                          <p style={{ fontSize: 13, color: '#334155', marginTop: 4 }}>{blog.keyPoints}</p>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <strong style={{ fontSize: 12, color: '#475569' }}>CTA:</strong>
                          <p style={{ fontSize: 13, color: '#334155', marginTop: 4 }}>{blog.cta}</p>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <strong style={{ fontSize: 12, color: '#475569' }}>Image Prompt:</strong>
                          <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>{blog.imagePrompt}</p>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <button
                            className="ch-btn ch-btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTopic(`Blog post: "${blog.title}" - ${blog.hook}`);
                              setBlogImagePrompt(blog.imagePrompt);
                              const blogSlug = blog.slug || blog.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                              setSourceUrl(`https://bigsystemone.com/blog/${blogSlug}`);
                              setSubTab('create');
                            }}
                          >
                            Use as Topic
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Schedule Modal */}
      {scheduleModal && (
        <div className="ch-schedule-overlay" onClick={() => setScheduleModal(null)}>
          <div className="ch-schedule-modal" onClick={e => e.stopPropagation()}>
            <h3>Schedule Post</h3>
            <label>Date and Time</label>
            <input
              type="datetime-local"
              value={scheduleDate}
              onChange={e => setScheduleDate(e.target.value)}
            />
            <div className="ch-schedule-actions">
              <button className="ch-btn" onClick={() => setScheduleModal(null)}>Cancel</button>
              <button
                className="ch-btn ch-btn-primary"
                onClick={confirmSchedule}
                disabled={!scheduleDate}
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentHub;
